/**
 * SpatialBreakdownBar -- "X% global · Y% regional · Z% local" header bar.
 *
 * Aggregates each plant's `spatial_breakdown.{scale}[metric]` across all
 * generators, normalizes to % shares, and renders as a stacked horizontal
 * bar with legend copy explaining the three scales.
 *
 * Used by: GeneratorAttributionCard (top of the card).
 */

import type { DisplacedGeneratorEnriched } from "../../model/domain/types";
import {
  MetricKey,
  METRIC_LABEL,
  fmtMetric,
  totalByScale,
} from "../helpers";

// ──────────────────────────────────────────────────────────────────────
// Scale colors — aligned with tab order Local / Regional / Global
// ──────────────────────────────────────────────────────────────────────

const SCALE_COLORS = {
  local: "#FBBF24",    // amber (near-field)
  regional: "#14B8A6", // teal (airshed)
  global: "#3B82F6",   // blue (atmospheric)
};

const SCALE_LABEL: Record<"local" | "regional" | "global", string> = {
  local: "Local",
  regional: "Regional",
  global: "Global",
};

const SCALE_DESC: Record<"local" | "regional" | "global", string> = {
  local:
    "Felt within ~50 km of the source. Most acute near the plant, decays rapidly with distance.",
  regional:
    "Felt across a ~100-1,500 km airshed or watershed downwind. Dominant for air-quality benefits.",
  global:
    "Atmospheric mixing — felt worldwide regardless of source location. Dominant for climate benefits.",
};

// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────

interface Props {
  plants: DisplacedGeneratorEnriched[];
  metric: MetricKey;
}

export function SpatialBreakdownBar({ plants, metric }: Props) {
  const local = totalByScale(plants, "local", metric);
  const regional = totalByScale(plants, "regional", metric);
  const global = totalByScale(plants, "global", metric);
  const total = local + regional + global;

  // If no spatial_breakdown at all (older runs), render nothing.
  if (total <= 0) return null;

  const pct = (v: number) => (v / total) * 100;

  return (
    <div className="mb-4">
      {/* Numeric readout */}
      <div className="flex items-baseline justify-between mb-2 gap-4">
        <div className="text-[11px] text-text-muted">
          <span className="font-medium text-text-secondary">
            {fmtMetric(total, metric)} {METRIC_LABEL[metric]} avoided
          </span>{" "}
          · split by scale at which the impact acts
        </div>
        <div className="flex gap-3 text-[11px] tabular-nums">
          <ScalePill
            color={SCALE_COLORS.global}
            label="Global"
            pct={pct(global)}
            desc={SCALE_DESC.global}
          />
          <ScalePill
            color={SCALE_COLORS.regional}
            label="Regional"
            pct={pct(regional)}
            desc={SCALE_DESC.regional}
          />
          <ScalePill
            color={SCALE_COLORS.local}
            label="Local"
            pct={pct(local)}
            desc={SCALE_DESC.local}
          />
        </div>
      </div>

      {/* Stacked bar (global | regional | local), ordered largest-scale-first */}
      <div className="flex w-full h-2 rounded-full overflow-hidden bg-surface-600">
        <div
          className="h-full"
          style={{ width: `${pct(global)}%`, backgroundColor: SCALE_COLORS.global }}
          title={`Global ${pct(global).toFixed(1)}% · ${fmtMetric(global, metric)} ${metric}`}
        />
        <div
          className="h-full"
          style={{ width: `${pct(regional)}%`, backgroundColor: SCALE_COLORS.regional }}
          title={`Regional ${pct(regional).toFixed(1)}% · ${fmtMetric(regional, metric)} ${metric}`}
        />
        <div
          className="h-full"
          style={{ width: `${pct(local)}%`, backgroundColor: SCALE_COLORS.local }}
          title={`Local ${pct(local).toFixed(1)}% · ${fmtMetric(local, metric)} ${metric}`}
        />
      </div>
    </div>
  );
}

function ScalePill({
  color,
  label,
  pct,
  desc,
}: {
  color: string;
  label: string;
  pct: number;
  desc: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1"
      title={`${SCALE_LABEL[label.toLowerCase() as keyof typeof SCALE_LABEL] ?? label}: ${desc}`}
    >
      <span
        className="w-2 h-2 rounded-full inline-block"
        style={{ backgroundColor: color }}
      />
      <span className="text-text-secondary">{label}</span>
      <span className="text-text-primary font-medium">{pct.toFixed(0)}%</span>
    </span>
  );
}

export { SCALE_COLORS };
